import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ArrowLeft, ChevronDown, Video, ShieldCheck } from "lucide-react";

const Check = ({ items, testid }) => (
  <div className="grid sm:grid-cols-2 gap-2" data-testid={testid}>
    {items.map((i) => (
      <div key={i.name} className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3">
        <span className="text-sm">{i.name}</span>
        <CheckCircle2 className="w-4 h-4 text-accent" />
      </div>
    ))}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="text-xs font-mono uppercase tracking-widest text-primary">{title}</h2>
    {children}
  </div>
);

export default function MetaAdsReview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/meta/review").then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  if (!data) return <div className="min-h-screen bg-background flex justify-center items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background grid-noise text-foreground">
      <header className="h-16 border-b border-border bg-card/40 backdrop-blur-md flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button data-testid="review-back" onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="font-display font-bold text-lg">Meta Ads Review Center</h1>
        </div>
        <span className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-accent/15 text-accent">SUBMISSION READY</span>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-8" data-testid="meta-review-page">
        <p className="text-sm text-muted-foreground">OTOSUBS Ads AI — App Review readiness. Each permission has a use case, test steps and expected result. Final approval is decided by Meta and cannot be guaranteed.</p>

        <Section title="Business Readiness"><Check items={data.readiness} testid="review-readiness" /></Section>
        <Section title="Connections"><Check items={data.connections} testid="review-connections" /></Section>

        <Section title="Permissions">
          <div className="space-y-2" data-testid="review-permissions">
            {data.permissions.map((p) => (
              <div key={p.key} className="bg-card border border-border rounded-xl overflow-hidden">
                <button data-testid={`perm-${p.key}`} onClick={() => setOpen(open === p.key ? null : p.key)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/40 transition-colors">
                  <span className="font-mono text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" />{p.key}</span>
                  <span className="flex items-center gap-2"><span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-accent/15 text-accent">{p.status}</span><ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open === p.key ? "rotate-180" : ""}`} /></span>
                </button>
                {open === p.key && (
                  <div className="px-4 py-4 border-t border-border space-y-3 text-sm">
                    <div><p className="text-xs text-muted-foreground">Why Required</p><p>{p.why_required}</p></div>
                    <div><p className="text-xs text-muted-foreground">Where Used</p><p>{p.where_used}</p></div>
                    <div><p className="text-xs text-muted-foreground">Test Account</p><p>{p.test_account}</p></div>
                    <div><p className="text-xs text-muted-foreground">Testing Steps</p>
                      <ol className="list-decimal list-inside text-muted-foreground">{p.testing_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
                    </div>
                    <div><p className="text-xs text-muted-foreground">Expected Result</p><p>{p.expected_result}</p></div>
                    <div className="flex items-center gap-2 text-primary text-xs"><Video className="w-4 h-4" /> Demo video attached</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Review Assets"><Check items={data.assets} testid="review-assets" /></Section>

        <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-xl" data-testid="review-back-bottom"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard</Button>
      </main>
    </div>
  );
}
